/**
 * References API
 *
 * Batch resolution of the work item and document references detected in chat
 * messages. Unresolvable references are absent from the response rather than
 * an error, so a partially resolvable batch still enriches what it can.
 */

import { api } from "./client";
import type {
  DecisionId,
  DocumentId,
  DocumentStatus,
  DocumentType,
  Priority,
  ProjectId,
  StatusCategory,
  WorkItemId,
} from "./types";

export interface WorkItemReference {
  id: WorkItemId;
  display_id: string;
  item_number: number;
  title: string;
  item_type: string;
  status_name: string;
  status_category: StatusCategory;
  priority: Priority;
  excerpt: string | null;
}

export interface DocumentReference {
  id: DocumentId;
  title: string;
  doc_type: DocumentType;
  status: DocumentStatus;
  excerpt: string | null;
}

export interface ResolvedReferences {
  work_items: WorkItemReference[];
  documents: DocumentReference[];
}

export interface ResolveReferencesRequest {
  work_item_display_ids: string[];
  work_item_ids: WorkItemId[];
  document_ids: DocumentId[];
}

export async function resolveReferences(
  projectId: ProjectId,
  body: ResolveReferencesRequest,
): Promise<ResolvedReferences> {
  return api.post<ResolvedReferences>(
    `/projects/${projectId}/references/resolve`,
    body,
  );
}

export interface DecisionReference {
  id: DecisionId;
  title: string;
  category: string;
  status: string;
  unconfirmed: boolean;
}

export interface ProjectSearchResults {
  work_items: WorkItemReference[];
  documents: DocumentReference[];
  decisions: DecisionReference[];
}

/** Cross-entity keyword search backing the command palette (FLOW-799). */
export async function searchProject(
  projectId: ProjectId,
  q: string,
  limit?: number,
): Promise<ProjectSearchResults> {
  const params = new URLSearchParams({ q });
  if (limit !== undefined) params.set("limit", String(limit));
  return api.get<ProjectSearchResults>(
    `/projects/${projectId}/search?${params.toString()}`,
  );
}
